import { ProductClass } from 'core/application/adminDashboard/benefitDetails/design/groupHra/productClass/benefitDesignClasses'
import { SelectClassValues } from 'core/application/adminDashboard/benefitDetails/design/productClass/selectClass'
import { FamilyStatus } from 'core/domain/companies/familyStatus'

import { NavItemProps } from '../../../../common/productClassWizardModal/models'
import CostSharingAmount, { CostSharingAmountProps } from './CostSharingAmount'
import CostSharingPercentage, { CostSharingPercentageProps } from './CostSharingPercentage'
import ExpenseReimbursement, { ExpenseReimbursementValues } from './ExpenseReimbursement'
import ProrateAllowances from './ProrateAllowances'
import SelectClass from './SelectClass'
import SetAllowance, { ProductClassAllowance } from './SetAllowance'
import WaitingPeriod from './WaitingPeriod'

export enum ProductClassStep {
  SelectClass = 'gchra-class-design-select-class',
  SetAllowance = 'gchra-class-design-allowance',
  ExpenseReimbursement = 'gchra-class-design-expense-reimbursement',
  CostSharingAmount = 'gchra-class-design-cost-sharing-amount',
  CostSharingPercentage = 'gchra-class-design-cost-sharing-percentage',
  WaitingPeriod = 'gchra-class-design-waiting-period',
  ProrateAllowances = 'gchra-class-design-prorate-allowances',
}

const getDeductibleAmountFromStateBasedOnFamilyStatus = (
  state: ProductClass,
  familyStatus: FamilyStatus,
): number | null => {
  return state.deductable !== null &&
    state.deductable &&
    state.offerSameAllowance !== null &&
    !state.offerSameAllowance &&
    state.productClassDeductible.length > 0
    ? state.productClassDeductible.find((f) => f.familyStatusId === familyStatus)?.amount ?? null
    : null
}

const getAllowanceAmountBasedOnFamilyStatus = (state: ProductClass, familyStatus: FamilyStatus): number | null => {
  return state.offerSameAllowance !== null && !state.offerSameAllowance && state.productClassAllowance.length > 0
    ? state.productClassAllowance.find((f) => f.familyStatusId === familyStatus)?.amount ?? null
    : null
}

export const NavItemsMapping: NavItemProps[] = [
  {
    key: ProductClassStep.SelectClass,
    component: SelectClass,
    nextButtonTooltip: 'admin-dashboard.benefit-details.class-design.select-class.next-tooltip',
    mapState: (state: ProductClass): SelectClassValues => {
      return {
        classType: state.classType !== null ? state.classType.id : null,
        productClassId: state.id ? state.id : null,
      }
    },
  },
  {
    key: ProductClassStep.SetAllowance,
    component: SetAllowance,
    nextButtonTooltip: 'admin-dashboard.benefit-details.class-design.set-allowances.next-tooltip',
    mapState: (state: ProductClass): ProductClassAllowance => {
      return {
        offerSameAllowance: state.offerSameAllowance,
        productClassId: state.id ? state.id : null,
        singleAmount: getAllowanceAmountBasedOnFamilyStatus(state, FamilyStatus.Single),
        singleWithDependeciesAmount: getAllowanceAmountBasedOnFamilyStatus(state, FamilyStatus.SingleWithDependents),
        marriedAmount: getAllowanceAmountBasedOnFamilyStatus(state, FamilyStatus.Married),
        marriedWithDependeciesAmount: getAllowanceAmountBasedOnFamilyStatus(state, FamilyStatus.MarriedWithDependents),

        sameAllowanceAmount:
          state.offerSameAllowance !== null && state.offerSameAllowance && state.productClassAllowance.length > 0
            ? state.productClassAllowance[0].amount
            : null,

        benefitAllowanceType: state.benefitAllowanceType,
        benefitStartDate: state.benefitStartDate,
        benefitEndDate: state.benefitEndDate,
      }
    },
  },
  {
    key: ProductClassStep.ExpenseReimbursement,
    component: ExpenseReimbursement,
    nextButtonTooltip: 'admin-dashboard.benefit-details.class-design.require-eob.next-tooltip',
    mapState: (productClassState: ProductClass): ExpenseReimbursementValues => {
      return {
        eobRequired: productClassState.requiresEOB !== null ? productClassState.requiresEOB : null,
        requiresPrescription: productClassState.requiresPrescription,
        expenseCategories: productClassState.eobExpense?.map((eob) => eob.id.toString()),
        productClassId: productClassState.id ? productClassState.id : null,
      }
    },
  },
  {
    key: ProductClassStep.CostSharingAmount,
    component: CostSharingAmount,
    nextButtonTooltip: 'admin-dashboard.benefit-details.class-design.hra-deductible.next-tooltip',
    mapState: (state: ProductClass): CostSharingAmountProps => {
      return {
        productClassId: state.id ? state.id : '',
        offerSameAllowance: state.offerSameAllowance ?? false,
        deductable: state.deductable,

        singleAmount: getDeductibleAmountFromStateBasedOnFamilyStatus(state, FamilyStatus.Single),
        singleWithDependentsAmount: getDeductibleAmountFromStateBasedOnFamilyStatus(
          state,
          FamilyStatus.SingleWithDependents,
        ),
        marriedAmount: getDeductibleAmountFromStateBasedOnFamilyStatus(state, FamilyStatus.Married),
        marriedWithDependentsAmount: getDeductibleAmountFromStateBasedOnFamilyStatus(
          state,
          FamilyStatus.MarriedWithDependents,
        ),

        sameDeductableAmount:
          state.deductable !== null &&
          state.deductable &&
          state.offerSameAllowance !== null &&
          state.offerSameAllowance &&
          state.productClassDeductible.length > 0
            ? state.productClassDeductible[0].amount
            : null,
      }
    },
  },
  {
    key: ProductClassStep.CostSharingPercentage,
    component: CostSharingPercentage,
    nextButtonTooltip: 'admin-dashboard.benefit-details.class-design.cost-sharing.next-tooltip',
    mapState: (state: ProductClass): CostSharingPercentageProps => {
      return {
        productClassId: state.id ? state.id : '',
        deductable: state.deductable ?? false,
        eECoinsurance: state.eeCoinsurance,
        eECoinsuranceAmount: state.eeCoinsuranceAmount,
      }
    },
  },
  {
    key: ProductClassStep.WaitingPeriod,
    component: WaitingPeriod,
    nextButtonTooltip: 'admin-dashboard.benefit-details.class-design.waiting-period.next-tooltip',
    mapState: (productClassState: ProductClass) => {
      return {
        waitingPeriod: productClassState.waitingPeriod,
        productClassId: productClassState.id,
      }
    },
  },
  {
    key: ProductClassStep.ProrateAllowances,
    component: ProrateAllowances,
    nextButtonTooltip: 'admin-dashboard.benefit-details.class-design.prorate-allowances.finish-tooltip',
    mapState: (productClassState: ProductClass) => {
      return {
        prorateAllowancesHire: productClassState.prorateAllowancesHire,
        prorateAllowancesTerminate: productClassState.prorateAllowancesTerminate,
        productClassId: productClassState.id,
      }
    },
  },
]
